import { useState } from "react";
import { AlertTriangle, Button, Checkbox, CircleAlert, ImagePlus, Info, Input, Pencil, Select, Sparkles, Tags, Trash2 } from "../../ui";
import { PARAM_INPUTS, fieldValue, pairText, stateText, withContext } from "./copilotModel";
import CopilotVerification from "./CopilotVerification";

function Problems({ entries, variant, icon: Icon }) {
  if (!entries?.length) return null;
  return (
    <ul className={`copilot-problems is-${variant}`}>
      {entries.map((entry, i) => (
        <li key={`${entry.code}-${i}`}>
          <Icon size={14} aria-hidden="true" /> <span>{entry.text || entry.code}</span>
        </li>
      ))}
    </ul>
  );
}

// One structured change of a plan: what it targets, the exact values, the
// affected entities (before -> after) and every consequence, from the
// backend preview. Edits are sent back as structured params, never text.
export default function CopilotOperationCard({ operation, t, lang, editable, busy, onEdit, onRemove, onExclude, verified = false, onVerify, onUploadImage }) {
  const { preview, type } = operation;
  const [showRows, setShowRows] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({});
  const [values, setValues] = useState({});
  const fields = withContext(preview.fields || []);
  const scalarParams = (operation.editable || []).filter((key) => PARAM_INPUTS[key]);
  const canExclude = editable && (operation.editable || []).includes("excludeIds");
  // AI estimates are edited in the verification panel instead.
  const needsVerification = Boolean(preview.requiresVerification);
  const canEditValues = editable && !needsVerification && (operation.editable || []).includes("values");
  const rows = preview.affected || [];
  const target = preview.target;

  const startEdit = () => {
    setDraft(Object.fromEntries(scalarParams.map((key) => [key, operation.params?.[key] ?? ""])));
    setValues({});
    setEditing(true);
    setShowRows(canEditValues || showRows);
  };

  const save = async () => {
    const params = {};
    for (const key of scalarParams) {
      const before = operation.params?.[key] ?? "";
      if (draft[key] !== before) params[key] = PARAM_INPUTS[key].type === "number" ? (draft[key] === "" ? null : Number(draft[key])) : draft[key];
    }
    const ok = await onEdit({ params, values });
    if (ok !== false) setEditing(false);
  };

  const renderInput = (key) => {
    const spec = PARAM_INPUTS[key];
    const label = t.fields[key] || key;
    if (spec.type === "select") {
      return <Select key={key} label={label} value={draft[key]} options={spec.options.map((value) => ({ value, label: t.values[value] || value }))} onChange={(e) => setDraft((d) => ({ ...d, [key]: e.target.value }))} />;
    }
    if (spec.type === "checkbox") return <Checkbox key={key} label={label} checked={Boolean(draft[key])} onChange={(e) => setDraft((d) => ({ ...d, [key]: e.target.checked }))} />;
    return <Input key={key} label={label} type={spec.type === "number" ? "number" : "text"} textarea={spec.type === "textarea"} dir="auto" value={draft[key] ?? ""} onChange={(e) => setDraft((d) => ({ ...d, [key]: e.target.value }))} />;
  };

  const generated = operation.generated || {};
  const valueKey = operation.params?.contentField === "description" ? (operation.params.contentLanguage === "ar" ? "descriptionAr" : "descriptionEn") : operation.params?.contentField === "name_translation" ? (operation.params.contentLanguage === "ar" ? "nameAr" : "nameEn") : null;

  return (
    <article className={`copilot-op ${preview.blocking?.length ? "is-blocked" : ""}`} aria-label={t.ops[type] || type}>
      <header className="copilot-op-head">
        <div className="copilot-op-title">
          <strong>{t.ops[type] || type}</strong>
          {target?.name && (
            <span dir="auto">
              {pairText(target.name, lang)}
              {target.context ? ` — ${pairText(target.context, lang)}` : ""}
            </span>
          )}
        </div>
        <div className="copilot-op-badges">
          {preview.aiGenerated && (
            <Tags variant="info" size="sm" icon={<Sparkles size={12} />}>
              {t.aiGenerated}
            </Tags>
          )}
          {needsVerification && (
            <Tags variant={verified ? "success" : "warning"} size="sm">
              {verified ? t.userVerified : t.needsVerification}
            </Tags>
          )}
          {operation.dependsOn?.length > 0 && <Tags variant="neutral" size="sm">{t.dependsOn(operation.dependsOn.map((i) => i + 1).join(", "))}</Tags>}
          {preview.affectedCount > 0 && <Tags variant="primary" size="sm">{t.affected(preview.affectedCount)}</Tags>}
          {preview.skippedCount > 0 && <Tags variant="neutral" size="sm">{t.skipped(preview.skippedCount)}</Tags>}
        </div>
      </header>

      {operation.notes?.length > 0 && <Problems entries={operation.notes} variant="info" icon={Info} />}

      {fields.length > 0 && !editing && (
        <dl className="copilot-op-fields">
          {fields.map((field) => (
            <div key={field.key} className="copilot-op-field">
              <dt>{t.fields[field.key] || field.key}</dt>
              <dd dir="auto">
                {field.before !== undefined && field.before !== null && (
                  <>
                    <span className="copilot-before">{fieldValue({ ...field, value: field.before }, t, lang)}</span>
                    <span className="copilot-arrow" aria-hidden="true">
                      {lang === "ar" ? " ← " : " → "}
                    </span>
                  </>
                )}
                <span>{fieldValue(field, t, lang)}</span>
              </dd>
            </div>
          ))}
        </dl>
      )}

      {editing && <div className="copilot-op-edit">{scalarParams.map(renderInput)}</div>}

      {needsVerification && (
        <CopilotVerification operation={operation} t={t} lang={lang} editable={editable} busy={busy} verified={verified} onVerify={onVerify} onEditValues={(changed) => onEdit({ params: {}, values: changed })} />
      )}

      {type === "SET_IMAGE" && (
        <div className="copilot-image">
          {operation.params?.image?.url && <img src={operation.params.image.url} alt="" className="copilot-image-preview" />}
          {operation.params?.image?.url && <small>{t.imageAttached}</small>}
          {editable && onUploadImage && (
            <label className={`ui-button primary soft sm rounded copilot-image-input ${busy ? "is-disabled" : ""}`}>
              <ImagePlus size={15} aria-hidden="true" /> {operation.params?.image?.url ? t.replaceImage : t.attachImage}
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp"
                disabled={busy}
                aria-label={t.attachImage}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  e.target.value = "";
                  if (file) onUploadImage(file);
                }}
              />
            </label>
          )}
        </div>
      )}

      <Problems entries={preview.consequences} variant="neutral" icon={Info} />
      <Problems entries={preview.warnings} variant="warning" icon={AlertTriangle} />
      <Problems entries={preview.blocking} variant="danger" icon={CircleAlert} />

      {!needsVerification && (rows.length > 0 || preview.skipped?.length > 0) && (
        <div className="copilot-op-rows">
          <Button type="button" variant="primary" appearance="text" size="xs" aria-expanded={showRows} onClick={() => setShowRows((v) => !v)}>
            {showRows ? t.hideAffected : t.viewAffected(preview.affectedCount + (preview.skippedCount || 0))}
          </Button>
          {showRows && (
            <ul className="copilot-rows">
              {rows.map((row) => {
                return (
                  <li key={row.id}>
                    <div className="copilot-row-main">
                      <span className="copilot-row-name" dir="auto">
                        {pairText(row.name, lang)}
                        {row.context && <small> · {pairText(row.context, lang)}</small>}
                      </span>
                      {(row.before || row.after) && (
                        <span className="copilot-row-change" dir="auto">
                          {row.before && <span className="copilot-before">{stateText(row.before, t, lang)}</span>}
                          {row.before && row.after && <span aria-hidden="true">{lang === "ar" ? " ← " : " → "}</span>}
                          {row.after && <span>{stateText(row.after, t, lang)}</span>}
                        </span>
                      )}
                      {row.items?.length > 0 && <small dir="auto">{row.items.map((i) => pairText(i.name, lang)).join(lang === "ar" ? "، " : ", ")}</small>}
                    </div>
                    {editing && canEditValues && valueKey && generated[row.id] && (
                      <Input label={t.fields[valueKey]} textarea dir="auto" value={values[row.id]?.[valueKey] ?? generated[row.id][valueKey] ?? ""} onChange={(e) => setValues((v) => ({ ...v, [row.id]: { ...(v[row.id] || {}), [valueKey]: e.target.value } }))} />
                    )}
                    {editing && canEditValues && operation.params?.contentField === "calories" && generated[row.id] && (
                      <Input label={t.fields.calories} type="number" value={values[row.id]?.caloriesKcal ?? generated[row.id].caloriesKcal ?? ""} onChange={(e) => setValues((v) => ({ ...v, [row.id]: { ...(v[row.id] || {}), caloriesKcal: e.target.value } }))} />
                    )}
                    {canExclude && onExclude && rows.length > 1 && !editing && (
                      <Button type="button" variant="warning" appearance="text" size="xs" disabled={busy} onClick={() => onExclude(row.id)}>
                        {t.exclude}
                      </Button>
                    )}
                  </li>
                );
              })}
              {(preview.skipped || []).map((row) => (
                <li key={`skip-${row.id}`} className="is-skipped">
                  <span className="copilot-row-name" dir="auto">
                    {pairText(row.name, lang)}
                    {row.context && <small> · {pairText(row.context, lang)}</small>}
                  </span>
                  <small>{t.reasons[row.reason] || row.reason}</small>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {editable && (
        <footer className="copilot-op-actions">
          {editing ? (
            <>
              <Button type="button" variant="primary" appearance="filled" size="xs" rounded loading={busy} onClick={save}>
                {t.save}
              </Button>
              <Button type="button" variant="neutral" appearance="soft" size="xs" rounded disabled={busy} onClick={() => setEditing(false)}>
                {t.discard}
              </Button>
            </>
          ) : (
            (scalarParams.length > 0 || canEditValues) && (
              <Button type="button" variant="primary" appearance="soft" size="xs" rounded icon={<Pencil size={13} />} disabled={busy} onClick={startEdit}>
                {t.edit}
              </Button>
            )
          )}
          {onRemove && !editing && (
            <Button type="button" variant="danger" appearance="text" size="xs" icon={<Trash2 size={13} />} disabled={busy} onClick={onRemove}>
              {t.remove}
            </Button>
          )}
        </footer>
      )}
    </article>
  );
}
